import React, { useEffect, useRef } from "react";
import { Psalm } from "../../chants/Psalm";
import { Padding } from "../../shared-components/Padding";
import { TweakProvider } from "../../utils/TweakProvider";
import { LegacyTextBlock } from "../pray/LegacyTextBlock";
import { OfficeHymn } from "../pray/OfficeHymn";
import { Spacer } from "../pray/Spacer";

export const RecordingPage: React.FC<{}> = () => {
    const mailRef = useRef<HTMLAnchorElement>(null);

    useEffect(() => {
        if (mailRef.current) {
            mailRef.current.href =
                "mailto:" + ["contact", "singtheoffice.com"].join("@");
        }
    }, []);

    return (
        <>
            <TweakProvider initialState={{}}>
                <Padding>
                    <div
                        style={{
                            fontWeight: "bold",
                            letterSpacing: 2,
                            textTransform: "uppercase",
                            fontSize: "140%",
                            paddingBottom: 4,
                            paddingTop: 32,
                            textAlign: "center",
                        }}
                    >
                        Recording Project
                    </div>
                </Padding>
                <LegacyTextBlock>
                    SingTheOffice is a website devoted to making it easier to
                    sing traditional Anglican Daily Offices, in particular the
                    plainsong chant of the Psalms and Canticles. It does this by
                    providing an automatically generated order of service for
                    Mattins, Evensong and Compline every day, with almost
                    everything than can be sung set out fully in square
                    notation. Alongside the main chants, options are provided
                    where the site will intone the chant for the user, helping
                    them to learn the pitches of the various chants.
                </LegacyTextBlock>
                <LegacyTextBlock>
                    This approach has been successful and well-received, with
                    many people saying that it tipped the balance for them in
                    making the chant of the office possible where they found it
                    too difficult before. For many, however, this has not gone
                    far enough, and they feel that they would need a recorded
                    voice to sing along with.
                </LegacyTextBlock>
                <LegacyTextBlock>
                    To this end, at SingTheOffice we believe that the goal of
                    more people singing the prayers of the church (especially
                    the Psalms) will be advanced by comissioning, and making
                    freely available on the site, a complete set of recordings
                    of the Psalms, Canticles, and other sung parts of the Book
                    of Common Prayer Daily Office.
                </LegacyTextBlock>
                <LegacyTextBlock>
                    <strong>
                        We are therefore looking to hire a single tenor who is
                        excited by this project and able to make the recordings
                        over the summer of 2021.
                    </strong>
                </LegacyTextBlock>
                <LegacyTextBlock>
                    We will need the following recordings:
                </LegacyTextBlock>
                <LegacyTextBlock>
                    <ul>
                        <li>
                            All of the Psalms sung as they are set out on the
                            site (one setting each).
                        </li>
                        <li>
                            All of the Canticles sung in ~4 settings per
                            Canticle.
                        </li>
                        <li>
                            All of the Office Hymns (approximately 20) sung.
                        </li>
                        <li>
                            The other sung parts of the Office (preces and
                            responses, etc) sung in one setting.
                        </li>
                    </ul>
                </LegacyTextBlock>
                <LegacyTextBlock>
                    Details of the project are as follows:
                </LegacyTextBlock>
                <LegacyTextBlock>
                    <ul>
                        <li>
                            We estimate that the total length of the recordings
                            will be approximately 15 hours. Most of the settings
                            are quite straightforward, with a few of the hymns
                            being more ornate.
                        </li>
                        <li>
                            <strong>
                                We are offering £2000 for the complete set of
                                recordings, paid upon completion.
                            </strong>
                        </li>
                        <li>
                            The content to be sung will be finalised by June
                            2021.
                        </li>
                        <li>
                            We can be flexible around how you wish to work. You
                            could work on this project full time for two weeks,
                            or complete it more slowly over the whole summer.
                            All that we ask is that you commit firmly up front
                            to a timetable that you agree with us.
                        </li>
                        <li>
                            We are looking for someone who can work from home,
                            or some other place arranged by them. We will cover
                            the cost of any equipment hire, if this is
                            necessary.
                        </li>
                        <li>
                            We are still finalising the details, but we are
                            expecting to use the Coverdale translations found in
                            the 1662 Book of Common Prayer International
                            Edition, which is a (very) slight modernisation of
                            the text found in the original 1662 BCP.
                        </li>
                        <li>
                            We are looking for someone who is excited about
                            creating a quality end product, with a lasting
                            legacy.
                        </li>
                        <li>
                            We will, of course, publicly credit you for your
                            work, and besides your monetary payment, you will
                            have the satisfaction of helping many people learn
                            to sing the prayers of the church over the coming
                            years.
                        </li>
                    </ul>
                </LegacyTextBlock>
                <LegacyTextBlock>
                    If you are interested in this work, please send a recording
                    of the following texts (using something like a Dropbox link)
                    to{" "}
                    <a type="mailto" ref={mailRef}>
                        our email address.
                    </a>{" "}
                    Guidelines for the recording are as follows:
                </LegacyTextBlock>
                <LegacyTextBlock>
                    <ul>
                        <li>
                            Sing at whatever pitch you find comfortable, and you
                            think others would generally find easiest to sing
                            along to.
                        </li>
                        <li>
                            Leave a decent pause between each verse, and at each
                            half point of the verse.
                        </li>
                        <li>
                            It doesn't matter whether these are all in one file,
                            split across multiple files, or whatever.
                        </li>
                        <li>
                            Record this using the best equipment you have easy
                            access to. Sound quality is not perticularly
                            important for this initial exercise.
                        </li>
                    </ul>
                </LegacyTextBlock>
                <LegacyTextBlock>
                    Enjoy, and we are looking forward to hearing from you!
                </LegacyTextBlock>
                <Psalm
                    psalmRef={{
                        type: "section",
                        number: 18,
                        firstVerse: 1,
                        lastVerse: 5,
                    }}
                    excludeGloriaPatri={true}
                />
                <Psalm
                    psalmRef={{
                        type: "section",
                        number: 22,
                        firstVerse: 1,
                        lastVerse: 5,
                    }}
                    excludeGloriaPatri={true}
                />
                <Psalm
                    psalmRef={{
                        type: "section",
                        number: 41,
                        firstVerse: 1,
                        lastVerse: 5,
                    }}
                    excludeGloriaPatri={true}
                />
                <Psalm
                    psalmRef={{
                        type: "section",
                        number: 51,
                        firstVerse: 1,
                        lastVerse: 5,
                    }}
                    excludeGloriaPatri={true}
                />
                <Psalm
                    psalmRef={{
                        type: "section",
                        number: 42,
                        firstVerse: 1,
                        lastVerse: 5,
                    }}
                    excludeGloriaPatri={true}
                />
                <Psalm
                    psalmRef={{
                        type: "section",
                        number: 105,
                        firstVerse: 1,
                        lastVerse: 5,
                    }}
                    excludeGloriaPatri={true}
                />
                <Psalm
                    psalmRef={{
                        type: "section",
                        number: 73,
                        firstVerse: 1,
                        lastVerse: 5,
                    }}
                    excludeGloriaPatri={true}
                />
                <Psalm
                    psalmRef={{
                        type: "section",
                        number: 141,
                        firstVerse: 1,
                        lastVerse: 5,
                    }}
                    excludeGloriaPatri={true}
                />
                <Psalm
                    psalmRef={{
                        type: "section",
                        number: 114,
                        firstVerse: 1,
                        lastVerse: 5,
                    }}
                    excludeGloriaPatri={true}
                />
                <OfficeHymn
                    verseLimit={4}
                    day={{
                        year: 2021,
                        date: 27,
                        dayOfTheWeek: "sat",
                        month: 3,
                        otherLabels: [],
                        collectId: "lent-5",
                        collect:
                            "We beseech thee, Almighty God, mercifully to look upon thy people; that by thy great goodness they may be governed and preserved evermore, both in body and soul; through Jesus Christ our Lord.",
                        weekOfTheYear: 12,
                        antiphonId: "lent",
                        dayOfTheYear: 85,
                        sentenceId: "passiontide",
                        hymnId: "passiontide-1",
                        easterOctave: false,
                        eveOf: {
                            year: 2021,
                            date: 28,
                            dayOfTheWeek: "sun",
                            month: 3,
                            label: "lent-6",
                            otherLabels: [],
                            collectId: "lent-6",
                            collect:
                                "Almighty and everlasting God, who, of thy tender love towards mankind, hast sent thy Son, our Savior Jesus Christ, to take upon him our flesh, and to suffer death upon the cross, that all mankind should follow the example of his great humility; Mercifully grant, that we may both follow the example of his patience, and also be made partakers of his resurrection; through the same Jesus Christ our Lord.",
                            weekOfTheYear: 13,
                            antiphonId: "lent",
                            dayOfTheYear: 86,
                            sentenceId: "passiontide",
                            hymnId: "passiontide-0",
                            easterOctave: false,
                        },
                        label: "undefined",
                    }}
                    hour="evensong"
                />
                <Spacer big />
            </TweakProvider>
        </>
    );
};
